import { z } from 'zod/v4';
import { createTool } from '../../tools';
import { WORKSPACE_TOOLS } from '../constants';
import { WorkspaceReadOnlyError } from '../errors';
import { emitWorkspaceMetadata, requireFilesystem } from './helpers';
import { startWorkspaceSpan } from './tracing';

export const deleteFileTool = createTool({
  id: WORKSPACE_TOOLS.FILESYSTEM.DELETE,
  description: 'Delete a file or directory from the workspace filesystem',
  outputSchema: z.string(),
  inputSchema: z.object({
    path: z.string().describe('The path to the file or directory to delete'),
    recursive: z
      .boolean()
      .optional()
      .default(false)
      .describe('If true, delete directories and their contents recursively. Required for non-empty directories.'),
  }),
  execute: async ({ path, recursive }, context) => {
    const { workspace, filesystem } = requireFilesystem(context);
    await emitWorkspaceMetadata(context, WORKSPACE_TOOLS.FILESYSTEM.DELETE);

    const span = startWorkspaceSpan(context, workspace, {
      category: 'filesystem',
      operation: 'delete',
      input: { path, recursive },
      attributes: { filesystemProvider: filesystem.provider },
    });

    try {
      if (filesystem.readOnly) {
        throw new WorkspaceReadOnlyError('delete');
      }

      const stat = await filesystem.stat(path);
      if (stat.type === 'directory') {
        await filesystem.rmdir(path, { recursive, force: recursive });
      } else {
        await filesystem.deleteFile(path);
      }

      span.end({ success: true });
      return `Deleted ${path}`;
    } catch (err) {
      span.error(err);
      throw err;
    }
  },
});
